import { useState } from 'react'

import { useLocation, useParams } from 'react-router'

import { Button } from '@tribeplatform/react-ui-kit/Button'
import {
  noop,
  useIsomorphicLayoutEffect,
} from '@tribeplatform/react-ui-kit/hooks'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { useAdminPanel } from '../../AdminPanel/index.js'
import { Form } from '../../Form/index.js'
import { T } from '../../i18n/components/T.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { useNavigate } from '../../Link/index.js'
import { MediaPicker } from '../../MediaPicker/MediaPicker.js'
import {
  FieldSortableItem,
  FieldSortableItemVariant,
} from '../../Views/components/FieldSortableItem.js'
import { GenericSettings } from '../../Views/components/GenericSettings.js'
import { ModelSettingSections } from '../components/ModelSettingSections.js'
import { useModelSettings } from '../hooks/useModelSettings.js'
import { CMSSharedState } from '../types.js'

import { FieldsSettings } from './FieldsSettings.js'
import { ReactionSettings } from './ReactionSettings.js'
import { ReactionsSettings } from './ReactionsSettings.js'

interface Props {
  isNew?: boolean
  goBack?: () => void
}
export const ModelSettings = ({ isNew = false, goBack }: Props) => {
  const { $t } = useI18n()
  const navigate = useNavigate()
  const { resetSharedState } = useAdminPanel<CMSSharedState>()
  const [selectedReactionId, setSelectedReactionId] = useState<string>(null)
  const { hash } = useLocation()
  const { slug } = useParams<{ slug: string }>()
  const {
    postType,
    replyTypes,
    defaultValues,
    replyTypeOptions,
    reactionItems,
    icon,
    isCreating,
    isUpdating,
    reactionError,
    checkIfReactionNew,
    setReaction,
    setReactionItems,
    handleSubmit,
    setIcon,
  } = useModelSettings(slug, isNew)

  useIsomorphicLayoutEffect(() => {
    resetSharedState()
  }, [resetSharedState])

  if (!postType && !isNew) {
    return null
  }

  return (
    <>
      <GenericSettings
        title={
          isNew
            ? $t({
                defaultMessage: 'New model',
                id: 'Admin.CMS.NewModel',
              })
            : postType.name
        }
        goBack={() => (goBack ? goBack() : navigate('/manage/content/cms'))}
      >
        <Form defaultValues={defaultValues} onSubmit={handleSubmit}>
          <ModelSettingSections>
            <ModelSettingSections.General>
              <Form.Input
                label={$t({
                  defaultMessage: 'Name',
                  id: 'Generics.Name.Noun',
                })}
                name="name"
                validation={{
                  required: $t({
                    defaultMessage: 'Please enter a name',
                    id: 'Admin.CMS.Name.Validation',
                  }),
                }}
                placeholder={$t({
                  defaultMessage: 'e.g. Discussion',
                  id: 'Admin.CMS.Name.Placeholder',
                })}
              />
              <MediaPicker
                value={icon}
                onChange={icon => {
                  setIcon(icon)
                }}
              />
            </ModelSettingSections.General>
            <ModelSettingSections.Interactions>
              {replyTypes.length > 0 && (
                <Form.Select
                  label={$t({
                    defaultMessage: 'Reply type',
                    id: 'Admin.CMS.ReplyType',
                  })}
                  name="validReplyTypeId"
                  items={replyTypeOptions}
                />
              )}
              <ReactionsSettings
                reactionItems={reactionItems}
                setReactionItems={setReactionItems}
                setSelectedReactionId={setSelectedReactionId}
                reactionError={reactionError}
              />
            </ModelSettingSections.Interactions>
            {!isNew && (
              <ModelSettingSections.Fields>
                <FieldSortableItem
                  id="modelFields"
                  label={$t({
                    defaultMessage: 'Manage model fields',
                    id: 'Admin.CMS.ManageModelFields',
                  })}
                  icon={<SvgIcon name="settings" />}
                  onClick={() => {
                    navigate(`/manage/content/cms/${postType?.slug}?#fields`)
                  }}
                  onToggle={noop}
                  draggable={false}
                  enabled
                  hasArrow
                  variant={FieldSortableItemVariant.NONE}
                />
              </ModelSettingSections.Fields>
            )}
          </ModelSettingSections>
          <div className="mt-5">
            <Button type="submit" loading={isCreating || isUpdating} fullWidth>
              {isNew ? (
                <T defaultMessage="Create" id="Generics.Create" />
              ) : (
                <T defaultMessage="Update" id="Generics.Update" />
              )}
            </Button>
          </div>
        </Form>
      </GenericSettings>
      {!!selectedReactionId && (
        <ReactionSettings
          reaction={reactionItems.find(({ id }) => id === selectedReactionId)}
          isNew={checkIfReactionNew(selectedReactionId)}
          checkIfReactionNew={checkIfReactionNew}
          setReaction={reaction => setReaction(selectedReactionId, reaction)}
          goBack={() => setSelectedReactionId(null)}
        />
      )}
      {hash === '#fields' && (
        <FieldsSettings
          postType={postType}
          goBack={() => navigate(`/manage/content/cms/${postType?.slug}`)}
        />
      )}
    </>
  )
}
